import { useEffect, useMemo, useState } from "react"
import { useActions } from "../../../hooks/useActions"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { Button, Empty, Flex, Space, Spin, Table, message } from "antd"
import useProjectDataColumns from "../hooks/UseProjectDataSQPColumns"
import { useSearchParams, useNavigate } from "react-router-dom"
import { routePath } from "../../../router/types"
import useProjectDataPrevWeekColumns from "../hooks/UseProjectDataSQPPrevWeekColumns"
import { IProjectDataSQP } from "../types/projectDataSQP.types"
import moment from "moment-timezone"

const ProjectDataSQPConteiner: React.FC = () => {
  const params = useSearchParams()[0]
  const projectId = params.get("projectId")

  const { getProjectDataSQP, getProjectDataSQPByLastWeek } = useActions()
  const { projectDataSQP, loading, error } = useTypedSelector((state) => state.projectDataSQP)
  const {
    projectDataSQPByLastWeek,
    loading: loadingDataByLastWeek,
    error: errorDataByLastWeek,
  } = useTypedSelector((state) => state.projectDataSQPByLastWeek)

  const navigate = useNavigate()
  useEffect(() => {
    if (!projectId) {
      navigate(routePath.PROJECTS)
      return
    }

    getProjectDataSQP({ projectId })
  }, [projectId])

  useEffect(() => {
    if (error) {
      message.error(error)
    }
  }, [error])

  const columns = useProjectDataColumns(Object.values(projectDataSQP).length)
  const columnsPrevWeek = useProjectDataPrevWeekColumns()

  function handleExpand(expanded: boolean, record: IProjectDataSQP) {
    if (expanded) {
      if (!projectId) {
        navigate(routePath.PROJECTS)
        return
      }

      getProjectDataSQPByLastWeek({
        projectId,
        searchQuery: record.searchTerm,
        lastDate: record.weekStartDate,
      })
    }
  }

  const projectDataSQPPrevWeeks = useMemo(() => {
    return function ProjectDataSQPPrevWeeks(record: IProjectDataSQP) {
      if (!projectDataSQPByLastWeek[record.searchTerm] && loadingDataByLastWeek) {
        return <Spin />
      } else if (!projectDataSQPByLastWeek[record.searchTerm]) {
        return <Empty />
      }

      const sortedSqp = projectDataSQPByLastWeek[record.searchTerm].sort(
        (a, b) => moment(a.weekStartDate).milliseconds() - moment(b.weekStartDate).milliseconds()
      )
      const lastRecord = sortedSqp[sortedSqp.length - 1]

      return (
        <Flex vertical justify="space-between" gap="small">
          <Table
            rowKey={"searchTerm"}
            dataSource={projectDataSQPByLastWeek[record.searchTerm]}
            columns={columnsPrevWeek}
            showHeader={false}
            virtual={true}
            pagination={false}
          />
          <Button
            color="primary"
            variant="dashed"
            style={{ width: "10%" }}
            onClick={() => handleExpand(true, lastRecord)}
            disabled={loadingDataByLastWeek}
          >
            <Space align="center">
              Next
              {loadingDataByLastWeek ? <Spin size="small" /> : null}
            </Space>
          </Button>
        </Flex>
      )
    }
  }, [projectDataSQPByLastWeek, loadingDataByLastWeek])

  return (
    <Table
      dataSource={Object.values(projectDataSQP).flat()}
      rowKey={"searchTerm"}
      columns={columns}
      virtual={true}
      loading={loading}
      pagination={false}
      scroll={{ x: "max-content", y: 55 * (window.innerHeight / 55) }}
      expandable={{
        expandedRowRender: (data) => {
          return projectDataSQPPrevWeeks(data)
        },
        onExpand: handleExpand,
      }}
    />
  )
}

export default ProjectDataSQPConteiner
